import { uid, ParameterValidator } from '@mvps-genesis/utilities';

export default class Message {
	
	// define static properties
	static types = {
		info: 'info',
		success: 'success',
		error: 'error',
		warning: 'warning'
	};

	static classes = {
		root: 'x-messages',
		type: 'x-messages--%type%',
		rootIconModifier: 'x-messages--with-icon',
		icon: 'x-messages__icon',
		iconFontPrefix: 'u-icon-%icon%',
		close: 'x-messages__close',
		autohide: 'auto-hide'
	};

	// define "private" properties
	_validator;

	// define "public" properties
	message;
	id;
	element;

	constructor(
		message,
		{
			type = 'info',
			closeable = false,
			autohide = false,
			autohideTime = 5000,
			icon = false,
			customClasses = '',
			customId = ''
		}
	) {

		// create validator
		this._validator = new ParameterValidator( this );

		// set message text
		this.message = this._validator.isString( 'message', message );

		// set parameters from options
		this.type = this._validator.isString( 'type', type );
		this.closeable = this._validator.isBoolean( 'closeable', closeable );
		this.autohide = this._validator.isBoolean( 'autohide', autohide );
		this.autohideTime = this._validator.isInt( 'autohideTime', autohideTime );
		this.icon = this._validator.isBoolean( 'icon', icon );
		this.customClasses = this._validator.isString( 'customClasses', customClasses );
		this.customId = this._validator.isString( 'customId', customId );

		// generate message id
		this.id = uid();

		// render message
		return this;

	}

	setElement( element ) {

		return this.element = this._validator.isElement( 'element', element );

	}

	setId( id ) {

		this.element.id = id;

	}

	close() {

		this.element.classList.add( 'hide' );

	}

	getType() {

		const type = Message.types[ this.type ];

		if ( type == undefined ) {
			throw new Error( 'Message::getType - Unsupported `type` option' );
		}

		return type;

	}

	getTypeClass() {

		return Message.classes.type.replace( '%type%', this.getType() );

	}

	getIconClass() {

		if ( !this.icon ) {
			return false;
		}

		return Message.classes.type.replace( '%icon%', this.icon );

	}

	render() {

		// create root element
		const element = document.createElement( 'div' );
		element.classList.add( Message.classes.root );
		element.classList.add( this.getTypeClass() );

		// set id
		let id = `js-message-${ this.id }`;
		if ( this.customId && this.customId.length > 0 ) {
			element.id = this.customId;
			element.datset.id = id;
		}
		else {
			element.id = id;
		}

		// set custom classes
		if ( this.customClasses ) {

			element.className += ` ${ this.customClasses }`;

		}

		// add close button
		if ( this.closeable ) {

			const closeElement = document.createElement( 'button' );
			closeElement.className = Message.classes.close;
			element.appendChild( closeElement );

		}
		
		// create icon element
		if ( this.icon ) {
			
			const iconElement = document.createElement( 'span' );
			iconElement.className = this.getIconClass();
			element.appendChild( iconElement );
			
		}
		
		// create message inner
		const messageInner = document.createElement( 'span' );
		messageInner.innerHTML = this.message;
		element.appendChild( messageInner );

		// add auto-hide class and time delay
		if ( this.autohide ) {

			element.classList.add( Message.classes.autohide );
			element.style.animationDelay = this.autohideTime;

		}

		this.setElement( element );
		this.postRender( element );

		return this.element;

	}

	postRender( element = this.element ) {

		if ( this.closeable ) {

			const closeElement = element.querySelector( Message.classes.close );
			closeElement.addEventListener( 'click', this.close.bind( this ), false );

		}
		
		if ( this.autohide ) {

			setTimeout( this.close.bind( this), (this.autohideTime + 500) );

		}

	}

	/**
	 * Create a message instance from a passed HTMLElement or valid selector
	 * @param {String} elementOrSelector 
	 */
	static fromHTML( elementOrSelector ) {

		const validator = new ParameterValidator( Message );
		const element = validator.isElement( 'elementOrSelector', elementOrSelector );
		const options = {
			closeable: ( element.dataset.closeable != undefined ),
			autohide: ( element.classList.contains( Message.classes.autohide ) ),
			customId: element.id
		};

		// identify type via classList
		for ( let type in Message.types ) {

			let typeClass = Message.classes.type.replace( '%type%', type );

			if ( element.classList.contains( typeClass ) ) {

				options.type = type;
				break;

			}

		}

		// autohide time
		if ( element.dataset.autohideTime != undefined ) {

			options.autohideTime = element.dataset.autohideTime;

		}

		const instance = new Message( element.innerHTML, options );
		instance.setElement( element );
		instance.setId( instance.id );
		instance.postRender( element );

		return instance;

	};

};